import {Component, Inject, OnInit, Optional} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {PaymentMethodEnum, PaymentTypeEnum, PurchaseTransaction} from "../../../../generated/sales-terminal-client";
import {AuthService} from "../../../../services/auth/auth.service";
import {SnackService} from "../../../../services/snack/snack.service";
import {Privileges} from "../../../admin/privileges/privileges.constants";
import {cloneDeep} from "lodash-es";
import {TotalsTable} from '../../../../components/totals-table/totals-table.component';

@Component({
  selector: 'app-transaction-refund-modal',
  templateUrl: './transaction-refund-modal.component.html',
  styleUrls: ['./transaction-refund-modal.component.scss']
})
export class TransactionRefundModalComponent implements OnInit {
  public ticketsTotalAmount: number = 0;
  public ticketsTotalSum: number = 0;
  public nonRefundableFee: number = 0;
  public refundableFee: number = 0;
  public transaction: PurchaseTransaction;
  public ticketIds: number[];
  public paymentMethod: PaymentMethodEnum;
  public tableData: TotalsTable;

  constructor(public dialogRef: MatDialogRef<TransactionRefundModalComponent>,
              private translateService: TranslateService,
              private snackService: SnackService,
              public authService: AuthService,
              @Optional() @Inject(MAT_DIALOG_DATA) public data: any) {
    this.transaction = this.data.transaction;
    this.ticketIds = this.data.ticketIds;
    this.paymentMethod = cloneDeep(this.transaction.payments.find((p) => p.type !== PaymentTypeEnum.GiftCard).method as PaymentMethodEnum || null);
    this.calculate();
  }

  ngOnInit(): void {
    if (this.transaction?.payments?.some(payment => payment.method?.startsWith(PaymentMethodEnum.EveryPay)) && !this.canDoPaymentReversal) {
      this.snackService.showInfo(this.translateService.instant('general.api_transaction_refund_payment_reversal_information_message'), true);
      this.paymentMethod = null;
    }
  }

  private calculate(): void {
    this.transaction.cart.events.forEach(event => {
      event.tickets.forEach(ticket => {
        if (this.ticketIds.includes(ticket.id)) {
          this.ticketsTotalAmount++;
          this.ticketsTotalSum += ticket.price;
          ticket.surcharges.forEach(fee => {
            if (!fee.refundable) {
              this.nonRefundableFee += fee.price;
            } else {
              this.refundableFee += fee.price;
            }
          });
        }
      });
    });
    this.fillDataTable();
  }

  get canDoPaymentReversal(): boolean {
    return this.transaction?.paymentReversalAllowed
      && this.authService.checkPrivileges(Privileges.SELF_SERVICE_TRANSACTIONS_PAYMENT_REVERSAL)
      && this.transaction.payments.find(value => value.type !== PaymentTypeEnum.GiftCard)?.type === PaymentTypeEnum.Transfer
      && this.transaction?.salespoint?.superCentreId === this.authService.user.superCentreId
      && this.transaction.payments.filter(payment => {
      return payment.type === PaymentTypeEnum.GiftCard;
    }).length === 0;
  }

  get totalSum(): number {
    return this.ticketsTotalSum + this.refundableFee;
  }

  public close(result?: boolean) {
    this.dialogRef.close({
      result,
      paymentMethod: this.paymentMethod
    });
  }

  public fillDataTable(): void {
    this.tableData = {
      body: [
        {label: 'general.api_transaction_refund_tickets_total_amount', value: this.ticketsTotalAmount, type: 'amount'},
        {label: 'general.api_transaction_refund_tickets_total_sum', value: this.ticketsTotalSum, type: 'price'}
      ],
      footer: [
        {label: 'general.api_transaction_refund_total_sum', value: this.totalSum, type: 'price'}
      ]
    };
    if (this.refundableFee) {
      this.tableData.body.push({label: 'general.api_transaction_refund_refundable_fee', value: this.refundableFee, type: 'price'});
    }
    if (this.nonRefundableFee) {
      this.tableData.body.push({label: 'general.api_transaction_refund_non_refundable_fee', value: this.nonRefundableFee, type: 'price'});
    }
    if (this.transaction.cart?.total?.cashRounding) {
      this.tableData.footer.push({label: 'general.api_cart_table_cash_rounding', value: this.transaction.cart.total.cashRounding, type: 'price', isPrefix: true});
      this.tableData.footer.push({label: 'general.api_transaction_refund_total_sum_in_cash', value: this.totalSum + this.transaction.cart.total.cashRounding, type: 'price'});
    }
  }

  protected readonly PaymentMethodValues = [PaymentMethodEnum.Transfer, PaymentMethodEnum.Card, PaymentMethodEnum.Cash];
  protected readonly PaymentMethodEnum = PaymentMethodEnum;
  protected readonly Privileges = Privileges;
}
